import PostCard from "../../components/post/PostCard.jsx"
import { PostList } from "../../components/post/PostList.jsx"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout.jsx"
import { useContext, useCallback, useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { UserContext } from "../../context/UserContext.js"
import { homeTimeline } from "../../controllers/feed/feed.js"
import { hashtagTimeline, publicTimeline, search } from "../../controllers/explore/explore.js"
import { useProfileComplete } from "../../hooks/useProfileComplete.js"
import { Avatar, Box, Button, Chip, CircularProgress, Divider, IconButton, Tab, Tabs, Typography } from "@mui/material"
import { AddCircleOutline, Close } from "@mui/icons-material"
import { CreatePost } from "../../components/post/CreatePost.jsx"
import { SearchWidget } from "../../components/search.widget.jsx"

export const Feed = () => {
    const { userDetails } = useContext(UserContext)
    const token = userDetails?.token ?? ''
    const navigate = useNavigate()
    useProfileComplete(userDetails)

    const [postOpen, setPostOpen] = useState(false)
    const [postStatus, setPostStatus] = useState('')
    // Defaults to public — always has content, unlike home which is empty until you follow people
    const [mode, setMode] = useState('public')

    // Search only applies to the Public tab — Friends stays exactly as it was.
    const [searchQuery, setSearchQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')
    const debounceRef = useRef(null)
    useEffect(() => {
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300)
        return () => clearTimeout(debounceRef.current)
    }, [searchQuery])
    const isSearching = mode === 'public' && debouncedQuery.length >= 2

    const { data: searchResults, isLoading: isSearchLoading } = useQuery({
        queryKey: ['search', debouncedQuery],
        queryFn: () => search(debouncedQuery, token),
        enabled: isSearching,
    })

    // Tapping a hashtag shows the posts actually tagged with it (its own
    // timeline, paginated the same way the public timeline is), replacing
    // whatever search/browse view was active.
    const [activeHashtag, setActiveHashtag] = useState(null)
    const openHashtag = (tag) => {
        setSearchQuery('')
        setActiveHashtag(tag)
    }
    const {
        data: hashtagData,
        fetchNextPage: fetchNextHashtagPage,
        hasNextPage: hasNextHashtagPage,
        isFetchingNextPage: isFetchingNextHashtagPage,
        isLoading: isHashtagLoading,
    } = useInfiniteQuery({
        queryKey: ['hashtagTimeline', activeHashtag],
        queryFn: ({ pageParam }) => hashtagTimeline(activeHashtag, token, pageParam),
        initialPageParam: null,
        getNextPageParam: (lastPage) => {
            if (!Array.isArray(lastPage) || lastPage.length === 0) return undefined
            return lastPage[lastPage.length - 1].id
        },
        enabled: !!activeHashtag,
    })
    const hashtagPosts = (hashtagData?.pages?.flat() ?? []).filter(p => p?.visibility !== 'direct' && !p?.in_reply_to_id)

    const {
        data: infiniteData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
    } = useInfiniteQuery({
        queryKey: [mode === 'home' ? 'homeTimeline' : 'getPublicTimeline', userDetails?.id],
        queryFn: ({ pageParam }) => mode === 'home' ? homeTimeline(token, pageParam) : publicTimeline(token, pageParam),
        initialPageParam: null,
        getNextPageParam: (lastPage) => {
            if (!Array.isArray(lastPage) || lastPage.length === 0) return undefined
            return lastPage[lastPage.length - 1].id
        },
    })

    // Replies show up in this same timeline API response — they belong in their
    // parent post's comment thread (see CommentDrawer), not as standalone cards here.
    const allPosts = (infiniteData?.pages?.flat() ?? []).filter(p => p?.visibility !== 'direct' && !p?.in_reply_to_id)

    // PostList/MediaCard (the grid used for Public browsing) take the same
    // {id, img} shape already established on the profile page's post grid —
    // a real media URL when present, otherwise the raw content (MediaCard
    // itself tells the two apart and renders text-only posts as a text tile).
    const toMediaItem = (item) => ({
        id: item.id,
        img: item.media_attachments?.length > 0 ? item.media_attachments[0].url : item.content,
    })

    // The layout's content area grows with its content (minHeight: 100vh) rather than
    // being height-bound, so the page itself scrolls — not this Box. Listen on the
    // window instead of the element so the load-more check actually fires.
    const handleScroll = useCallback(() => {
        const scrollHeight = document.documentElement.scrollHeight
        const scrollTop = window.scrollY
        const clientHeight = window.innerHeight
        if (scrollHeight - scrollTop - clientHeight >= 400) return
        if (activeHashtag) {
            if (hasNextHashtagPage && !isFetchingNextHashtagPage) fetchNextHashtagPage()
        } else if (!isSearching && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
        }
    }, [activeHashtag, hasNextHashtagPage, isFetchingNextHashtagPage, fetchNextHashtagPage, isSearching, fetchNextPage, hasNextPage, isFetchingNextPage])

    useEffect(() => {
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [handleScroll])

    return (
        <MainLoggedInLayout>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Typography variant="h5" fontWeight={900} sx={{
                    background: 'linear-gradient(90deg,#E40303,#FF8C00,#008026,#24408E,#732982)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                }}>
                    The Tea
                </Typography>
                <Button
                    onClick={() => setPostOpen(true)}
                    startIcon={<AddCircleOutline />}
                    variant="outlined"
                    size="small"
                    sx={{
                        borderRadius: 50,
                        textTransform: 'none',
                        fontWeight: 700,
                        borderColor: '#732982',
                        color: '#732982',
                        '&:hover': { background: 'rgba(115,41,130,0.06)', borderColor: '#732982' },
                    }}
                >
                    New Post
                </Button>
            </Box>

            {/* Mode toggle */}
            <Tabs
                value={mode}
                onChange={(_, v) => setMode(v)}
                sx={{
                    mb: 2,
                    minHeight: 40,
                    "& .MuiTab-root": { textTransform: "none", fontWeight: 700, minHeight: 40 },
                    "& .MuiTabs-indicator": { background: "linear-gradient(90deg,#E40303,#732982)" },
                }}
            >
                <Tab label="Public" value="public" />
                <Tab label="Friends" value="home" />
            </Tabs>

            {/* Search — Public tab only; Friends is untouched */}
            {mode === 'public' && (
                <Box sx={{ mb: 2 }}>
                    <SearchWidget
                        search={searchQuery}
                        setSearch={(v) => { setSearchQuery(v); if (activeHashtag) setActiveHashtag(null) }}
                        placeholder="Search people, posts, hashtags…"
                    />
                </Box>
            )}

            {/* Post list */}
            <Box>
                {activeHashtag ? (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#732982' }}>#{activeHashtag}</Typography>
                            <IconButton size="small" onClick={() => setActiveHashtag(null)}>
                                <Close fontSize="small" />
                            </IconButton>
                        </Box>
                        {isHashtagLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <CircularProgress sx={{ color: '#732982' }} />
                            </Box>
                        ) : (
                            <>
                                <PostList media={hashtagPosts.map(toMediaItem)} />
                                <Box sx={{ py: 3, textAlign: 'center' }}>
                                    {isFetchingNextHashtagPage ? (
                                        <CircularProgress size={22} sx={{ color: '#732982' }} />
                                    ) : hasNextHashtagPage ? (
                                        <Typography variant="caption" color="text.secondary">Scroll for more</Typography>
                                    ) : hashtagPosts.length > 0 ? (
                                        <Typography variant="caption" color="text.disabled">You're all caught up</Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">No posts tagged #{activeHashtag} yet.</Typography>
                                    )}
                                </Box>
                            </>
                        )}
                    </>
                ) : isSearching ? (
                    isSearchLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress sx={{ color: '#732982' }} />
                        </Box>
                    ) : (
                        <>
                            {searchResults?.accounts?.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#732982', textTransform: 'uppercase', letterSpacing: 0.4, mb: 1.5 }}>
                                        People
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {searchResults.accounts.map((acc) => (
                                            <Box
                                                key={acc.id}
                                                onClick={() => navigate('/profile/' + acc.id)}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', p: 1, borderRadius: 2, '&:hover': { background: 'rgba(115,41,130,0.06)' } }}
                                            >
                                                <Avatar src={acc.avatar} sx={{ width: 40, height: 40 }} />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontWeight: 700, fontSize: 14 }} noWrap>{acc.display_name || acc.username}</Typography>
                                                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>@{acc.acct}</Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {searchResults?.hashtags?.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#732982', textTransform: 'uppercase', letterSpacing: 0.4, mb: 1.5 }}>
                                        Hashtags
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {searchResults.hashtags.map((tag) => (
                                            <Chip
                                                key={tag.name}
                                                label={'#' + tag.name}
                                                onClick={() => openHashtag(tag.name)}
                                                sx={{ background: 'rgba(115,41,130,0.1)', color: '#732982', fontWeight: 700 }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {searchResults?.statuses?.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#732982', textTransform: 'uppercase', letterSpacing: 0.4, mb: 1.5 }}>
                                        Posts
                                    </Typography>
                                    <PostList
                                        media={searchResults.statuses
                                            .filter(item => item?.visibility !== 'direct' && !item?.in_reply_to_id)
                                            .map(toMediaItem)}
                                    />
                                </Box>
                            )}

                            {!searchResults?.accounts?.length && !searchResults?.hashtags?.length && !searchResults?.statuses?.length && (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
                                    No results for "{debouncedQuery}"
                                </Typography>
                            )}
                        </>
                    )
                ) : isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: '#732982' }} />
                    </Box>
                ) : mode === 'public' ? (
                    <>
                        <PostList media={allPosts.filter(item => item?.content || item?.media_attachments?.length > 0).map(toMediaItem)} />
                        <Box sx={{ py: 3, textAlign: 'center' }}>
                            {isFetchingNextPage ? (
                                <CircularProgress size={22} sx={{ color: '#732982' }} />
                            ) : hasNextPage ? (
                                <Typography variant="caption" color="text.secondary">Scroll for more</Typography>
                            ) : allPosts.length > 0 ? (
                                <Typography variant="caption" color="text.disabled">You're all caught up</Typography>
                            ) : (
                                <Typography variant="body2" color="text.secondary">No posts yet.</Typography>
                            )}
                        </Box>
                    </>
                ) : (
                    <>
                        {allPosts.map((item, i) =>
                            (item?.content || item?.media_attachments?.length > 0) && (
                                <Box key={item.id ?? i} sx={{ mb: 1.5 }}>
                                    <PostCard content={item} token={token} />
                                    {i < allPosts.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                                </Box>
                            )
                        )}
                        <Box sx={{ py: 3, textAlign: 'center' }}>
                            {isFetchingNextPage ? (
                                <CircularProgress size={22} sx={{ color: '#732982' }} />
                            ) : hasNextPage ? (
                                <Typography variant="caption" color="text.secondary">Scroll for more</Typography>
                            ) : allPosts.length > 0 ? (
                                <Typography variant="caption" color="text.disabled">You're all caught up</Typography>
                            ) : (
                                <Typography variant="body2" color="text.secondary">No posts yet.</Typography>
                            )}
                        </Box>
                    </>
                )}
            </Box>

            <CreatePost
                token={token}
                open={postOpen}
                setOpen={setPostOpen}
                status={postStatus}
                setStatus={setPostStatus}
                buttonTitle="Post"
                visibility="public"
            />
        </MainLoggedInLayout>
    )
}
